"use client";

import type { PromoCard } from "@/lib/content/types";

import Link from "next/link";
import { motion } from "framer-motion";
import { cn } from "@/lib/cn";
import { EASE } from "@/lib/motion";
import { Icon } from "@/components/ui/Icon";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { useContent } from "@/components/shell/ContentContext";
import { useRail } from "./Rail";

/* `copy` off draws each card as its image alone — for designed cards whose
   art already carries the offer's words — the whole card linking where its
   button would. */
export function PromoCarousel({ cards, copy = true }: { cards: PromoCard[]; copy?: boolean }) {
  const rail = useRail();

  if (cards.length === 0) return null;

  return (
    <div className="group/promo flex items-center gap-[16px]">
      <button
        onClick={() => rail.scrollByPage(-1)}
        disabled={rail.state.start}
        aria-label="Previous promotion"
        className={cn(
          "hidden h-[46px] w-[46px] shrink-0 place-items-center rounded-full bg-elevation-2 transition-colors md:grid",
          rail.state.start ? "cursor-not-allowed text-fg-disabled" : "text-fg hover:bg-elevation-3",
        )}
      >
        <Icon name="arrow-left" size={20} />
      </button>

      <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-[16px]">
        <div className="w-full">
          <div
            ref={rail.ref}
            onScroll={rail.measure}
            className="scrollbar-none mask-fade-r-lg flex snap-x snap-mandatory gap-[16px] overflow-x-auto overflow-y-hidden scroll-smooth"
          >
            {cards.map((p, i) => (
              <motion.article
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: EASE, delay: i * 0.06 }}
                className="flex h-[236px] w-[460px] max-w-full shrink-0 snap-start flex-col items-center justify-center gap-[12px] overflow-hidden rounded-2xl bg-elevation-2"
              >
                {!copy ? (
                  <Link href={p.href} aria-label={p.title || p.cta || "Open"} className="relative block h-full w-full overflow-hidden rounded-xl">
                    {p.imageUrl ? (
                      <RemoteImage src={p.imageUrl} width={460} height={236} />
                    ) : (
                      <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-fg opacity-10">
                        <Icon name="image" size={44} />
                      </span>
                    )}
                  </Link>
                ) : (
                <div className="surface-sheen relative flex w-full flex-1 flex-col items-start justify-center gap-[16px] overflow-hidden rounded-xl p-[24px]">
                  {p.imageUrl ? (
                    <>
                      <RemoteImage src={p.imageUrl} width={460} height={236} />
                      <span className="absolute inset-0 bg-gradient-to-t from-scrim-strong via-scrim to-transparent" />
                    </>
                  ) : (
                    <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-fg opacity-10">
                      <Icon name="image" size={44} />
                    </span>
                  )}
                  <span className="relative flex h-[14px] items-center justify-center gap-[2px] rounded-sm bg-elevation-3 px-[3px] text-[9px] font-bold leading-[1.5] text-fg">
                    {p.tag}
                  </span>
                  <div className="relative flex w-full flex-col items-start justify-center gap-[12px]">
                    <div className="flex w-full flex-col items-start gap-[2px]">
                      <h3 className="w-[300px] max-w-full truncate text-[15.75px] font-extrabold leading-[1.6] text-fg lg:text-[17.5px]">
                        {p.title}
                      </h3>
                      <p className="w-[300px] max-w-full text-[14px] font-medium leading-[1.6] text-fg-subtle">
                        {p.body}
                      </p>
                    </div>
                    <Link
                      href={p.href}
                      className="flex h-[38px] items-center justify-center gap-[8px] rounded-lg bg-brand px-[12px] text-[12px] font-bold leading-[1.6] text-fg-on-primary transition-colors hover:bg-brand-bold"
                    >
                      {p.cta}
                    </Link>
                  </div>
                </div>
                )}
              </motion.article>
            ))}
          </div>
        </div>

        {/* One dot per position the rail can rest at — not per card: with
            cards wider than the leftover range the tail cards share the end
            position, and a dot nothing can land on would make every arrow
            click look like it skipped. */}
        <div className="flex h-[24px] w-full items-center justify-center">
          {rail.state.pages > 1 &&
            Array.from({ length: rail.state.pages }).map((_, i) => (
              <button
                key={i}
                onClick={() => rail.scrollToPage(i)}
                aria-label={`Page ${i + 1}`}
                className="grid h-[24px] w-[24px] place-items-center"
              >
                <span
                  className={cn(
                    "block h-[4px] rounded-sm transition-all duration-300",
                    i === rail.state.page
                      ? "w-[20px] bg-brand"
                      : "w-[10px] bg-elevation-3 hover:bg-elevation-4",
                  )}
                />
              </button>
            ))}
        </div>
      </div>

      <button
        onClick={() => rail.scrollByPage(1)}
        disabled={rail.state.end}
        aria-label="Next promotion"
        className={cn(
          "hidden h-[46px] w-[46px] shrink-0 place-items-center rounded-full bg-elevation-2 transition-colors md:grid",
          rail.state.end ? "cursor-not-allowed text-fg-disabled" : "text-fg hover:bg-elevation-3",
        )}
      >
        <Icon name="arrow-right" size={20} />
      </button>
    </div>
  );
}
